import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cropper from "react-easy-crop";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { BoxPreview } from "@/components/BoxPreview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { UploadCloud } from "lucide-react";
import { useAuth } from "@/utils/auth";
import { boxSizes } from "@/data/boxSizes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const boxStyles = [
  {
    label: "RSC / Shipping Box",
    value: "RSC / Shipping Box",
    testId: "box-style-rsc-shipping-box",
  },
];
const materials = [
  {
    label: "Kraft/32ECT-C",
    value: "Kraft/32ECT-C",
    testId: "material-kraft-32ect-c",
  },
];

// the setup fee is still applied to the computed price but not shown separately
const SETUP_FEE = 35;

const availableSizes = boxSizes.map((size) => ({
  ...size,
  label: `${size.length} x ${size.width} x ${size.height}`,
}));

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.crossOrigin = "anonymous";
    image.src = url;
  });

const getCroppedImage = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return imageSrc;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );
  return canvas.toDataURL("image/png");
};

const autoTrimImage = async (imageSrc) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return imageSrc;
  ctx.drawImage(image, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let found = false;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = (y * width + x) * 4;
      const [r, g, b, a] = [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
      const isWhite = r > 240 && g > 240 && b > 240;
      if (a > 10 && !isWhite) {
        found = true;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  if (!found) return imageSrc;

  const trimmedCanvas = document.createElement("canvas");
  const trimmedWidth = maxX - minX + 1;
  const trimmedHeight = maxY - minY + 1;
  trimmedCanvas.width = trimmedWidth;
  trimmedCanvas.height = trimmedHeight;
  const trimmedCtx = trimmedCanvas.getContext("2d");
  if (!trimmedCtx) return imageSrc;
  trimmedCtx.drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);
  return trimmedCanvas.toDataURL("image/png");
};

export default function BuilderPage() {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [dimensions, setDimensions] = useState({ length: 12, width: 9, height: 4 });
  const [boxStyle, setBoxStyle] = useState(boxStyles[0].value);
  const [material, setMaterial] = useState(materials[0].value);
  const [quantity, setQuantity] = useState(500);
  const [artworkScale, setArtworkScale] = useState(1);
  const [artworkDataUrl, setArtworkDataUrl] = useState("");
  const [rawArtworkDataUrl, setRawArtworkDataUrl] = useState("");
  const [artworkName, setArtworkName] = useState("");
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [contact, setContact] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderId, setOrderId] = useState("");

  const sizeMatch = useMemo(() => {
    const length = Number(dimensions.length);
    const width = Number(dimensions.width);
    const height = Number(dimensions.height);
    if (!length || !width || !height) return null;

    const exact = availableSizes.find(
      (size) => size.length === length && size.width === width && size.height === height,
    );
    if (exact) {
      return { type: "exact", match: exact };
    }

    const fits = availableSizes.filter(
      (size) => size.length >= length && size.width >= width && size.height >= height,
    );
    const pickClosest = (list) =>
      list.reduce((prev, current) => {
        const prevDiff = Math.abs(prev.length - length) + Math.abs(prev.width - width) + Math.abs(prev.height - height);
        const currDiff = Math.abs(current.length - length) + Math.abs(current.width - width) + Math.abs(current.height - height);
        return currDiff < prevDiff ? current : prev;
      });

    const suggestion = fits.length
      ? fits.reduce((prev, current) => {
          const prevDiffs = [prev.length - length, prev.width - width, prev.height - height];
          const currDiffs = [current.length - length, current.width - width, current.height - height];
          const prevMax = Math.max(...prevDiffs);
          const currMax = Math.max(...currDiffs);
          if (currMax !== prevMax) {
            return currMax < prevMax ? current : prev;
          }
          const prevSum = prevDiffs.reduce((acc, value) => acc + value, 0);
          const currSum = currDiffs.reduce((acc, value) => acc + value, 0);
          return currSum < prevSum ? current : prev;
        })
      : pickClosest(availableSizes);

    return { type: "suggested", match: suggestion };
  }, [dimensions]);

  const activeSize = sizeMatch?.match;
  const minOrderQty = activeSize?.minOrderQty || 1;
  const isQuantityValid = quantity >= minOrderQty && quantity % minOrderQty === 0;

  const estimatedPrice = useMemo(() => {
    if (activeSize?.priceEach) {
      return Math.round((activeSize.priceEach * quantity + SETUP_FEE) * 100) / 100;
    }
    const volume = dimensions.length * dimensions.width * dimensions.height;
    const base = volume * 0.04 + quantity * 0.32;
    return Math.round((base + SETUP_FEE) * 100) / 100;
  }, [activeSize, dimensions, quantity]);

  // we no longer display unit price or setup fee separately
  // we don't show per-unit price, so no calculation needed
  const effectiveUnitPrice = null;

  const isSizeSuggested = sizeMatch?.type === "suggested";

  const handleAcceptSuggestedSize = () => {
    if (!sizeMatch?.match) return;
    setDimensions({
      length: sizeMatch.match.length,
      width: sizeMatch.match.width,
      height: sizeMatch.match.height,
    });
  };

  const cropAspect = useMemo(() => {
    return dimensions.height ? dimensions.length / dimensions.height : 1;
  }, [dimensions]);

  const handleDimensionChange = (key) => (event) => {
    const value = Number(event.target.value);
    setDimensions((prev) => ({ ...prev, [key]: Number.isNaN(value) ? 0 : value }));
  };

  const handleContactChange = (key) => (event) => {
    setContact((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (loadEvent) => {
      const result = loadEvent.target?.result || "";
      const trimmed = await autoTrimImage(result);
      setArtworkDataUrl(trimmed);
      setRawArtworkDataUrl(trimmed);
      setArtworkName(file.name);
      setArtworkScale(1);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleApplyCrop = async () => {
    if (!rawArtworkDataUrl || !croppedAreaPixels) return;
    const croppedImage = await getCroppedImage(rawArtworkDataUrl, croppedAreaPixels);
    const trimmed = await autoTrimImage(croppedImage);
    setArtworkDataUrl(trimmed);
    setRawArtworkDataUrl(trimmed);
    setArtworkScale(1);
    setIsCropOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSizeSuggested) {
      toast.error("Please confirm the suggested size before submitting.");
      return;
    }
    if (!isQuantityValid) {
      toast.error(`Quantity must be in increments of ${minOrderQty}.`);
      return;
    }
    if (!isAuthenticated) {
      toast.error("Please log in to submit your order.");
      return;
    }
    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: "openid profile email",
        },
      });
      const payload = {
        order: {
          customer_name: contact.name,
          email: contact.email,
          company: contact.company,
          phone: contact.phone,
          box_style: boxStyle,
          material,
          length: dimensions.length,
          width: dimensions.width,
          height: dimensions.height,
          quantity,
          artwork_data_url: artworkDataUrl || null,
          artwork_scale: artworkScale,
          notes: contact.notes,
        },
      };
      const response = await axios.post(`${API}/payments/create-checkout-session`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Unable to start payment", {
        description: "Please review your details and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const canceled = params.get("canceled");
    if (canceled) {
      toast.error("Payment canceled. You can try again anytime.");
      params.delete("canceled");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <div className="relative" data-testid="builder-page">
      <div className="grain-overlay" data-testid="builder-grain-overlay" />
      <SiteNav mode="app" />

      <main className="px-6 py-12">
        <div className="mx-auto w-full max-w-6xl space-y-10">
          <div className="space-y-3">
            <Badge
              className="rounded-none border-2 border-black bg-white text-xs font-bold uppercase tracking-[0.35em] text-primary shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
              data-testid="builder-badge"
            >
              3D box builder
            </Badge>
            <h1 className="text-4xl font-bold uppercase" data-testid="builder-title">
              Customize your box and preview artwork scale.
            </h1>
            <p className="text-sm text-muted-foreground" data-testid="builder-subtitle">
              Adjust sizing, upload your graphic, and enlarge or shrink placement before production.
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.1fr_1.4fr]">
            <form className="space-y-6" onSubmit={handleSubmit} data-testid="builder-form">
              <Card className="rounded-none border-2 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold uppercase" data-testid="specs-title">
                    Box specs
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label data-testid="length-label">Length (in)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={dimensions.length}
                        onChange={handleDimensionChange("length")}
                        data-testid="length-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label data-testid="width-label">Width (in)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={dimensions.width}
                        onChange={handleDimensionChange("width")}
                        data-testid="width-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label data-testid="height-label">Height (in)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={dimensions.height}
                        onChange={handleDimensionChange("height")}
                        data-testid="height-input"
                      />
                    </div>
                  </div>
                  {sizeMatch?.type === "suggested" ? (
                    <Alert
                      className="rounded-none border-2 border-black bg-white"
                      data-testid="size-match-alert"
                    >
                      <AlertTitle
                        className="text-xs font-semibold uppercase tracking-[0.3em]"
                        data-testid="size-match-title"
                      >
                        Size unavailable
                      </AlertTitle>
                      <AlertDescription
                        className="text-sm text-muted-foreground"
                        data-testid="size-match-description"
                      >
                        We don’t offer {dimensions.length} × {dimensions.width} × {dimensions.height}. Suggested:
                        <span className="font-semibold text-primary" data-testid="size-match-suggestion">
                          {" "}{sizeMatch.match.label}
                        </span>
                        . Min order/increment: {sizeMatch.match.minOrderQty}. Click OK to apply this size.
                      </AlertDescription>
                      <div className="mt-3 flex justify-end">
                        <Button
                          type="button"
                          className="h-9 rounded-none border-2 border-black bg-primary px-4 text-[10px] uppercase tracking-[0.3em] text-primary-foreground"
                          onClick={handleAcceptSuggestedSize}
                          data-testid="size-match-accept-button"
                        >
                          OK
                        </Button>
                      </div>
                    </Alert>
                  ) : null}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label data-testid="box-style-label">Box style</Label>
                      <Select value={boxStyle} onValueChange={setBoxStyle}>
                        <SelectTrigger data-testid="box-style-select">
                          <SelectValue placeholder="Select box style" />
                        </SelectTrigger>
                        <SelectContent data-testid="box-style-options">
                          {boxStyles.map((style) => (
                            <SelectItem
                              key={style.value}
                              value={style.value}
                              data-testid={style.testId}
                            >
                              {style.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label data-testid="material-label">Material</Label>
                      <Select value={material} onValueChange={setMaterial}>
                        <SelectTrigger data-testid="material-select">
                          <SelectValue placeholder="Select material" />
                        </SelectTrigger>
                        <SelectContent data-testid="material-options">
                          {materials.map((stock) => (
                            <SelectItem
                              key={stock.value}
                              value={stock.value}
                              data-testid={stock.testId}
                            >
                              {stock.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label data-testid="quantity-label">Quantity</Label>
                    <Input
                      type="number"
                      min={minOrderQty}
                      step={minOrderQty}
                      value={quantity}
                      onChange={(event) => setQuantity(Number(event.target.value))}
                      data-testid="quantity-input"
                    />
                    <p className="text-xs text-muted-foreground" data-testid="quantity-helper">
                      Orders must be in increments of {minOrderQty}.
                    </p>
                    {!isQuantityValid ? (
                      <p className="text-xs text-red-500" data-testid="quantity-error">
                        Enter a quantity of {minOrderQty} or a multiple of {minOrderQty}.
                      </p>
                    ) : null}
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-none border-2 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold uppercase" data-testid="artwork-title">
                    Artwork placement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Label data-testid="upload-label">Upload graphic</Label>
                      <p className="text-xs text-muted-foreground" data-testid="upload-helper">
                        PNG or JPG. White areas are treated as transparent and auto-trimmed.
                      </p>
                      <p className="text-xs text-muted-foreground" data-testid="crop-helper">
                        Use Crop to trim extra borders before scaling.
                      </p>
                    </div>
                    <label
                      className="flex cursor-pointer items-center gap-2 rounded-none border-2 border-black bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-primary transition-colors duration-200 hover:bg-secondary"
                      data-testid="upload-button"
                    >
                      <UploadCloud className="h-4 w-4" />
                      Upload artwork
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleUpload}
                        className="hidden"
                        data-testid="artwork-upload-input"
                      />
                    </label>
                  </div>
                  {artworkName ? (
                    <div
                      className="flex items-center justify-between rounded-none border-2 border-dashed border-black bg-secondary/20 px-4 py-3 text-xs uppercase tracking-[0.3em]"
                      data-testid="artwork-file-name"
                    >
                      <span>{artworkName}</span>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 rounded-none border-2 border-black px-3 text-[10px] uppercase tracking-[0.3em]"
                          onClick={() => {
                            setCrop({ x: 0, y: 0 });
                            setZoom(1);
                            setIsCropOpen(true);
                          }}
                          data-testid="artwork-crop-button"
                        >
                          Crop
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 rounded-none border-2 border-black px-3 text-[10px] uppercase tracking-[0.3em]"
                          onClick={() => {
                            setArtworkDataUrl("");
                            setRawArtworkDataUrl("");
                            setArtworkName("");
                          }}
                          data-testid="artwork-remove-button"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  <div className="space-y-3">
                    <Label data-testid="scale-label">Artwork scale (max = 1/2" margin)</Label>
                    <Slider
                      value={[artworkScale]}
                      min={0.2}
                      max={1}
                      step={0.05}
                      onValueChange={(value) => setArtworkScale(value[0])}
                      data-testid="artwork-scale-slider"
                    />
                    <div
                      className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-muted-foreground"
                      data-testid="scale-readout"
                    >
                      <span>Smaller</span>
                      <span>{Math.round(artworkScale * 100)}%</span>
                      <span>Max size</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-none border-2 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold uppercase" data-testid="contact-title">
                    Order details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label data-testid="name-label">Full name</Label>
                      <Input
                        value={contact.name}
                        onChange={handleContactChange("name")}
                        data-testid="name-input"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label data-testid="email-label">Work email</Label>
                      <Input
                        type="email"
                        value={contact.email}
                        onChange={handleContactChange("email")}
                        data-testid="email-input"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label data-testid="company-label">Company</Label>
                      <Input
                        value={contact.company}
                        onChange={handleContactChange("company")}
                        data-testid="company-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label data-testid="phone-label">Phone</Label>
                      <Input
                        value={contact.phone}
                        onChange={handleContactChange("phone")}
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label data-testid="notes-label">Production notes</Label>
                    <Textarea
                      value={contact.notes}
                      onChange={handleContactChange("notes")}
                      data-testid="notes-input"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-none border-2 border-black bg-secondary/20">
                <CardContent className="space-y-4 pt-6">
                  <div
                    className="rounded-none border-2 border-black bg-white px-4 py-3 text-xs uppercase tracking-[0.3em] text-primary"
                    data-testid="shipping-disclaimer"
                  >
                    Ships in ~10 business days from order date.
                  </div>
                  <div className="flex items-center justify-between" data-testid="summary-line-1">
                    <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                      Estimated quote
                    </span>
                    <span className="text-2xl font-semibold">
                      ${estimatedPrice.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>  
                  {/* setup fee and per‑unit breakdown hidden */}
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-muted-foreground">
                    <span data-testid="summary-line-2">Material: {material}</span>
                    <span data-testid="summary-line-3">Qty: {quantity}</span>
                  </div>
                  {!isAuthenticated ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-12 w-full rounded-none border-2 border-black bg-white text-xs font-bold uppercase tracking-[0.3em] text-primary"
                      onClick={() => loginWithRedirect({ appState: { returnTo: "/builder" } })}
                      data-testid="login-to-order-button"
                    >
                      Log in to submit order
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    className="h-12 w-full rounded-none border-2 border-black bg-primary text-xs font-bold uppercase tracking-[0.3em] text-primary-foreground shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-transform duration-200 hover:-translate-y-0.5"
                    disabled={isSubmitting || isSizeSuggested || !isQuantityValid}
                    data-testid="submit-order-button"
                  >
                    {isSubmitting ? "Redirecting" : "Pay & submit order"}
                  </Button>
                  {orderId ? (
                    <div
                      className="border-2 border-black bg-white px-4 py-3 text-xs uppercase tracking-[0.3em]"
                      data-testid="order-confirmation"
                    >
                      Order confirmed · ID {orderId.slice(0, 8).toUpperCase()}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </form>

            <div className="space-y-6">
              <Card className="h-[520px] rounded-none border-2 border-black bg-white">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold uppercase" data-testid="preview-title">
                    3D print preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-[420px]">
                  <BoxPreview
                    dimensions={dimensions}
                    material={material}
                    artworkUrl={artworkDataUrl}
                    artworkScale={artworkScale}
                  />
                </CardContent>
              </Card>
              <Card className="rounded-none border-2 border-black">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold uppercase" data-testid="preview-notes-title">
                    Preview notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p data-testid="preview-note-1">
                    Drag the box to rotate. Scroll or pinch to zoom.
                  </p>
                  <p data-testid="preview-note-2">
                    Artwork scale controls the decal size on the front panel.
                  </p>
                  <p data-testid="preview-note-3">
                    Final proofs are shared after order submission.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={isCropOpen} onOpenChange={setIsCropOpen}>
        <DialogContent
          className="max-w-2xl rounded-none border-2 border-black"
          data-testid="crop-dialog"
        >
          <DialogHeader>
            <DialogTitle data-testid="crop-dialog-title">Crop artwork</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="relative h-72 w-full overflow-hidden rounded-none border-2 border-black bg-black"
              data-testid="cropper-container"
            >
              {rawArtworkDataUrl ? (
                <Cropper
                  image={rawArtworkDataUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={cropAspect}
                  onCropChange={setCrop}
                  onCropComplete={handleCropComplete}
                  onZoomChange={setZoom}
                />
              ) : null}
            </div>
            <div className="space-y-2">
              <Label data-testid="crop-zoom-label">Zoom</Label>
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.1}
                onValueChange={(value) => setZoom(value[0])}
                data-testid="crop-zoom-slider"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-none border-2 border-black px-4 text-xs uppercase tracking-[0.3em]"
              onClick={() => setIsCropOpen(false)}
              data-testid="crop-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-10 rounded-none border-2 border-black bg-primary px-4 text-xs uppercase tracking-[0.3em] text-primary-foreground"
              onClick={handleApplyCrop}
              data-testid="crop-apply-button"
            >
              Apply crop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SiteFooter />
    </div>
  );
}